import type { CaptureResult } from 'posthog-js';

interface ExceptionFrame {
  filename?: string;
  source?: string;
  abs_path?: string;
}

interface ExceptionEntry {
  value?: string;
  stacktrace?: {
    frames?: ExceptionFrame[];
  };
}

function isExceptionList(value: unknown): value is ExceptionEntry[] {
  return Array.isArray(value);
}

function frameLocations(frame: ExceptionFrame): string[] {
  return [frame.filename, frame.source, frame.abs_path].filter(
    (location): location is string => typeof location === 'string',
  );
}

function frameMatchesThirdPartyNoise(frame: ExceptionFrame): boolean {
  return frameLocations(frame).some(
    (location) =>
      location.includes('/static/javascripts/client_default') ||
      location.includes('client_default_') ||
      location.includes('client.crisp.chat') ||
      location.includes('/crisp/') ||
      location.includes('/_vercel/insights/'),
  );
}

function isThirdPartyException(exception: ExceptionEntry): boolean {
  return exception.stacktrace?.frames?.some(frameMatchesThirdPartyNoise) ?? false;
}

function isSupabaseLockException(exception: ExceptionEntry): boolean {
  return (
    exception.value?.includes('Lock broken') ||
    exception.value?.includes('was released because another request stole') ||
    false
  );
}

export function filterPostHogExceptionNoise(cr: CaptureResult | null): CaptureResult | null {
  if (!cr || cr.event !== '$exception') return cr;

  // PostHog's native error-tracking frames currently store script URLs in
  // `filename`; keep `source`/`abs_path` support for older or custom payloads.
  const exceptionList = cr.properties?.$exception_list as unknown;
  if (!isExceptionList(exceptionList) || exceptionList.length === 0) return cr;

  const firstException = exceptionList[0];
  if (!firstException) return cr;

  if (isThirdPartyException(firstException) || isSupabaseLockException(firstException)) {
    return null;
  }

  return cr;
}
